import { useInfiniteQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { Plus } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { DataList, DetailPanel, DetailPlaceholder, type Column } from "@/components/DataList";
import type { ListTools } from "@/components/ListToolbar";
import { Badge } from "@/components/ui";
import { ImportPanel } from "@/features/import/ImportPanel";
import {
  isReturnScope,
  listPurchaseReturns,
  purchaseReturnDetailQuery,
  purchaseReturnKeys,
  purchaseReturnListQuery,
  returnScopes,
  returnStatusTone,
  type ListParams,
  type ReturnRow,
  type ReturnScope,
} from "@/features/purchase-returns/api";
import { PurchaseReturnPanel } from "@/features/purchase-returns/ReturnPanel";
import { activeCompanyOf, useActiveCompany } from "@/lib/activeCompany";
import { cn } from "@/lib/cn";
import { EXPORT_PAGE, type ExportColumn } from "@/lib/exportXlsx";
import { useFormat } from "@/lib/format";
import { sessionContextQuery, useCan } from "@/lib/session";

// Procurement → Orders → Supplier returns (PX-PUR-120, ADR-054): the active
// company's returns to suppliers, keyset through purchase_return_page (open /
// all, search on number, supplier or the supplier's RMA); the detail is
// purchase_return_document in one round-trip, prefetched on hover. New: from
// a received purchase order ("Return to supplier"), by the order's number, or
// from an NCR (return to supplier). Import (entity purchase_returns, drafts)
// next to Export (ADR-034).

interface Search {
  scope: ReturnScope;
  q: string;
  r?: string | undefined;
  /** New return: the purchase order it is for. */
  order?: string | undefined;
  import?: "purchase_returns" | undefined;
}

export const Route = createFileRoute("/_app/procurement/supplier-returns")({
  validateSearch: (raw: Record<string, unknown>): Search => {
    const s: Search = { scope: isReturnScope(raw.scope) ? raw.scope : "open", q: typeof raw.q === "string" ? raw.q.slice(0, 60) : "" };
    if (typeof raw.r === "string" && raw.r && raw.r.length <= 64) s.r = raw.r;
    if (s.r === "new" && typeof raw.order === "string" && raw.order.length <= 64) s.order = raw.order;
    if (raw.import === "purchase_returns") s.import = raw.import;
    return s;
  },
  loaderDeps: ({ search }) => ({ scope: search.scope, q: search.q }),
  loader: async ({ context, deps }) => {
    const ctx = await context.queryClient.ensureQueryData(sessionContextQuery);
    const companyId = activeCompanyOf(ctx)?.id;
    if (companyId) await context.queryClient.ensureInfiniteQueryData(purchaseReturnListQuery({ companyId, scope: deps.scope, q: deps.q }));
  },
  component: ReturnsPage,
});

function ReturnsPage() {
  const { t } = useTranslation();
  const tr = t as unknown as (key: string) => string;
  const fmt = useFormat();
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const queryClient = useQueryClient();
  const { company } = useActiveCompany();
  const canManage = useCan("procurement.return.manage");
  const canImport = useCan("core.data.import") && canManage;
  const setSearch = useCallback(
    (patch: Partial<Search>) => void navigate({ search: (prev) => ({ ...prev, ...patch }), replace: true }),
    [navigate],
  );
  const params: ListParams = { companyId: company?.id ?? "", scope: search.scope, q: search.q };
  const query = useInfiniteQuery(purchaseReturnListQuery(params));
  const [text, setText] = useState(search.q);
  useEffect(() => {
    if (text === search.q) return;
    const id = setTimeout(() => setSearch({ q: text }), 200);
    return () => clearTimeout(id);
  }, [text, search.q, setSearch]);

  const columns: Column<ReturnRow>[] = [
    {
      key: "number",
      header: t("prt.colNumber"),
      width: "minmax(130px,0.8fr)",
      cell: (r, sel) => (
        <span className={cn("font-mono text-xs", sel && "font-semibold text-accent-ink", !r.number && "italic text-ink-muted")}>
          {r.number ?? t("prt.draft")}
        </span>
      ),
    },
    {
      key: "supplier",
      header: t("prt.supplier"),
      width: "minmax(180px,1.4fr)",
      cell: (r) => (
        <span className="truncate">
          <span className="font-mono text-xs">{r.supplier_code}</span> <span className="text-ink-soft">{r.supplier_name}</span>
        </span>
      ),
    },
    { key: "order", header: t("prt.order"), width: "minmax(130px,0.8fr)", className: "font-mono text-xs", cell: (r) => r.order_number },
    { key: "ref", header: t("prt.supplierRma"), width: "minmax(110px,0.8fr)", className: "truncate text-ink-soft", cell: (r) => r.supplier_rma ?? "—" },
    { key: "lines", header: t("prt.colLines"), width: "minmax(60px,0.3fr)", align: "end", className: "tabular", cell: (r) => r.line_count },
    { key: "created", header: t("prt.colCreated"), width: "minmax(90px,0.5fr)", className: "tabular text-ink-soft", cell: (r) => fmt.day(r.created_at.slice(0, 10)) },
    {
      key: "status",
      header: t("prt.colStatus"),
      width: "minmax(130px,0.7fr)",
      cell: (r) => <Badge tone={returnStatusTone[r.status]}>{tr(`prt.status.${r.status}`)}</Badge>,
    },
  ];
  const exportColumns: ExportColumn<ReturnRow>[] = [
    { header: "number", kind: "code", value: (r) => r.number },
    { header: "status", kind: "text", value: (r) => r.status },
    { header: "supplier", kind: "code", value: (r) => r.supplier_code },
    { header: "order", kind: "code", value: (r) => r.order_number },
    { header: "supplier_rma", kind: "text", value: (r) => r.supplier_rma },
    { header: "lines", kind: "number", value: (r) => String(r.line_count) },
    { header: "created_at", kind: "date", value: (r) => r.created_at.slice(0, 10) },
  ];
  const listTools: ListTools<ReturnRow> = {
    id: "purchase-returns",
    title: t("prt.title"),
    onNew: canManage && company ? () => setSearch({ r: "new", order: undefined, import: undefined }) : undefined,
    onImport: canImport ? () => setSearch({ import: "purchase_returns", r: undefined }) : undefined,
    export: { fileBase: "supplier-returns", sheet: t("prt.title"), columns: exportColumns, fetchPage: (cursor, signal) => listPurchaseReturns(params, cursor, signal, EXPORT_PAGE) },
  };
  const chip = (pressed: boolean) =>
    cn(
      "h-8 rounded-full border px-3 text-[13px] transition-colors",
      pressed ? "border-ink bg-ink font-medium text-surface" : "border-border bg-surface text-ink hover:bg-surface-hover",
    );

  return (
    <div className="flex min-h-0 grow flex-col gap-4">
      <header className="flex flex-wrap items-end gap-3">
        <div className="flex grow flex-col gap-0.5">
          <nav aria-label="Breadcrumb" className="text-[13px] text-ink-muted">
            {t("nav.procurement.title")} <span aria-hidden="true">/</span> {t("nav.procurement.supplierReturns")}
          </nav>
          <h1 className="text-[28px] font-semibold leading-9 tracking-[-0.02em]">{t("prt.title")}</h1>
        </div>
        {canManage && company && (
          <button
            type="button"
            onClick={() => setSearch({ r: "new", order: undefined, import: undefined })}
            className="inline-flex h-10 items-center gap-1.5 rounded-xl bg-accent pl-3 pr-4 font-medium text-on-accent hover:bg-accent-hover"
          >
            <Plus size={18} strokeWidth={2} aria-hidden="true" />
            {t("prt.new")}
          </button>
        )}
      </header>
      <div className="flex flex-wrap items-center gap-2">
        <div role="group" aria-label={t("prt.colStatus")} className="flex flex-wrap gap-2">
          {returnScopes.map((s) => (
            <button key={s} type="button" aria-pressed={search.scope === s} onClick={() => setSearch({ scope: s })} className={chip(search.scope === s)}>
              {tr(`prt.scope.${s}`)}
            </button>
          ))}
        </div>
        <input
          type="search"
          aria-label={t("prt.search")}
          placeholder={t("prt.search")}
          value={text}
          onChange={(e) => setText(e.target.value)}
          className="ml-auto h-8 w-56 rounded-lg border border-border bg-surface px-2.5 text-[13px] placeholder:text-ink-muted"
        />
      </div>
      <div className="flex min-h-0 grow gap-4">
        <DataList
          label={t("prt.listLabel")}
          columns={columns}
          query={query}
          rowId={(r) => r.id}
          selectedId={search.r}
          onSelect={(r) => setSearch({ r: r.id, order: undefined, import: undefined })}
          onClear={() => setSearch({ r: undefined, order: undefined })}
          onPrefetch={(r) => void queryClient.prefetchQuery(purchaseReturnDetailQuery(r.id))}
          emptyTitle={t("prt.emptyTitle")}
          emptyBody={t("prt.emptyBody")}
          minWidth={820}
          tools={listTools}
        />
        <DetailPanel
          label={search.import ? t("import.title") : t("prt.detailLabel")}
          open={!!search.r || !!search.import}
          placeholder={<DetailPlaceholder title={t("prt.selectPrompt")} hint={t("prt.selectPromptHint")} />}
        >
          {search.import ? (
            <ImportPanel
              entities={["purchase_returns"]}
              initial="purchase_returns"
              companyId={company?.id}
              onClose={() => setSearch({ import: undefined })}
              invalidate={[purchaseReturnKeys.all]}
            />
          ) : (
            search.r &&
            company && (
              <PurchaseReturnPanel
                key={`${search.r}-${search.order ?? ""}`}
                id={search.r}
                orderId={search.order}
                companyId={company.id}
                onClose={() => setSearch({ r: undefined, order: undefined })}
                onOpen={(id) => setSearch({ r: id, order: undefined })}
                onPickOrder={(orderId) => setSearch({ r: "new", order: orderId })}
              />
            )
          )}
        </DetailPanel>
      </div>
    </div>
  );
}
